'use client'

import Link from 'next/link'
import { ROUTES } from '@/constants'
import { SliderWithNavigation } from '@/components/v3/molecules/custom-sliders/slider-with-navigation/slider-with-navigation'
import { navItemOnClickGTMEvent } from '@/utils/gtmEvents/navItemOnClickGTMEvent'
import type { ShopMegaMenuData } from '@/types/api/shop-mega-menu'
import { ShopMegaMenuCard } from './shop-mega-menu-card'
// The heading block is the category menus' own — same wording ("View all"),
// same position, same type — so it is reused rather than restyled here. The
// navigation already crosses modules this way (see NavSubcategoryList).
import nav from '../main-nav/main-nav.module.css'
import s from './nav-shop-mega-menu.module.scss'

type Props = {
  data: ShopMegaMenuData
  // Closes the menu after a click that navigates away.
  onNavigate: () => void
}

// The Shop dropdown: two columns of collection links on the left, a black
// FEATURED slider on the right. Rendered inside the shared `.dropdown` wrapper
// in MainNav, so panel positioning and the open animation are the category
// menus'.
export const NavShopMegaMenu = ({ data, onNavigate }: Props) => (
  <div className={nav.dropdownWrapper}>
    <div className={nav.dropdownNavWrapper}>
      <Link
        href={ROUTES.SHOP.ROOT}
        className={nav.subcategoryTitleWrapper}
        onClick={() => {
          onNavigate()
          navItemOnClickGTMEvent('Shop')
        }}
      >
        <span className={nav.subcategoryTitle}>Shop</span>
        <span className={nav.subcategoryTitleText}>View all</span>
      </Link>

      <div className={s.columns}>
        {data.columns.map((column, index) =>
          column.length === 0 ? null : (
            <ul key={index} className={s.column}>
              {column.map((link) => (
                <li key={link.slug} className={s.columnItem}>
                  <Link
                    href={ROUTES.SHOP.COLLECTION(link.slug)}
                    className={s.columnLink}
                    onClick={() => {
                      onNavigate()
                      navItemOnClickGTMEvent(link.title)
                    }}
                  >
                    {link.title}
                  </Link>
                </li>
              ))}
            </ul>
          )
        )}
      </div>
    </div>

    {data.featured.length > 0 && (
      <div className={s.featured}>
        <SliderWithNavigation
          title={<span className={s.featuredTitle}>Featured</span>}
          // Three cards to a panel, per the Figma frame. The slider sizes them
          // (a third of the panel each, 24 apart) rather than CSS, so a short
          // list keeps the same card width and simply leaves space to its right
          // instead of stretching to fill the panel.
          keenSliderOptions={{ slides: { perView: 3, spacing: 24 } }}
          // Past three cards there is something to scroll to, which is what the
          // chevrons in the Figma heading are.
          navigationVisible={data.featured.length > 3}
          navigationIconsColor="#fff"
          slidesWrapperClassName={s.featuredSlides}
        >
          {data.featured.map((collection) => (
            <ShopMegaMenuCard
              key={collection.id}
              collection={collection}
              onNavigate={onNavigate}
            />
          ))}
        </SliderWithNavigation>
      </div>
    )}
  </div>
)
